import { HttpErrorResponse } from "@angular/common/http";
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core";
import { StateService, UIRouterGlobals } from "@uirouter/core";
import { cloneDeep, orderBy } from "lodash";
import { forkJoin, of } from "rxjs";
import { catchError, finalize, map, mergeMap, tap } from "rxjs/operators";
import { environment as appEnv } from "../../environments/environment";
import { AttributeService } from "../models/attribute.service";
import { DeployService } from "../models/deploy.service";
import { DiffStatsService } from "../models/diff-stats.service";
import { TimeDurAgoService } from "../time-dur-ago/time-dur-ago.service";
import { DeployHelperService } from "./deploy-helper.service";

@Component({
  selector: "ab-deploy-list",
  templateUrl: "./deploy-list.component.html",
})
export class DeployListComponent implements OnChanges, OnInit {
  public appEnv = appEnv;
  public chartData: any[];
  public column: string;
  public currentPage: number;
  public deploys: any;
  public environments: string[];
  public envsWithNoDeploys: any;
  public loading: boolean;
  public sortDesc: boolean;
  public totalDeploys: any;
  public totalPages: number;

  @Input() public chartType: string;
  @Input() public dur: string;
  @Input() public environment: string;
  @Input() public featureDisabled: boolean;
  @Input() public lastN: number | string;
  @Input() public page: number;
  @Input() public project: any;
  @Input() public emptyProject: boolean;
  @Output() public loadingChanged = new EventEmitter<number>();

  // Note: If you change the number of deploys per page, make sure to change
  // REVISION_PAIRS_LIMIT in airbrake-app (https://goo.gl/KgxiCq) because this is
  // the maximum number of Diff Stats airbrake-ng can fetch from airbrake-app per
  // request.
  private PAGESIZE = 30;

  constructor(
    private $state: StateService,
    private $uiRouterGlobals: UIRouterGlobals,
    private attributeService: AttributeService,
    private deployService: DeployService,
    private deployHelperService: DeployHelperService,
    private diffStatsService: DiffStatsService,
    private timeDurAgoService: TimeDurAgoService,
  ) {}

  public hasPreviousPage() {
    return this.currentPage > 1;
  }

  public hasNextPage() {
    return this.currentPage < this.totalPages;
  }

  public isDisabled(env: string) {
    return this.envsWithNoDeploys.indexOf(env) > -1;
  }

  public sortBy(column: string) {
    this.sortDesc =
      column === this.column ? !this.sortDesc : this.defaultSortDesc(column);

    this.column = column;

    this.deploys = this.sortDeploys(this.deploys, this.column, this.sortDesc);
  }

  public ngOnChanges(_changes: SimpleChanges) {
    if (this.featureDisabled || this.deploys == null) {
      return;
    }

    this.loadPage();
  }

  public updateChart(params: any) {
    this.$state.go("project-deploys", params);
  }

  public trackById(_index: number, item: any) {
    return item.id;
  }

  public trackByIndex(index: number, _item: any) {
    return index;
  }

  public ngOnInit() {
    this.column = "createdAt";
    this.sortDesc = this.defaultSortDesc(this.column);
    this.envsWithNoDeploys = [];
    this.chartData = [];

    if (this.featureDisabled) {
      this.environments = ["production"];
      this.displayPage(this.buildSampleDeploys());
      this.loading = false;
    } else {
      this.loadEnvironments(this.project.id)
        .pipe(
          tap(() =>
            this.findEnvsWithNoDeploys(this.project.id, this.environments),
          ),
          tap(() => this.loadPage()),
        )
        .subscribe();
    }
  }

  private defaultSortDesc(column: string) {
    // For the default order, users most likely want to see the largest or most
    // recent first, except for alpha where A-Z makes more sense.
    return ["username", "revision", "environment"].indexOf(column) === -1;
  }

  private sortDeploys(deploys: any[], column: string, sortDesc: boolean) {
    return orderBy(deploys, [column], [sortDesc ? "desc" : "asc"]);
  }

  private displayPage(data: any) {
    this.deploys = data.deploys || [];
    this.totalDeploys = data.count;
    this.totalPages = Math.ceil(data.count / this.PAGESIZE);
    this.currentPage = data.page;

    if (this.deploys.length === 0 && this.totalDeploys > 0) {
      this.page = this.totalPages;
      return this.$state.go(this.$uiRouterGlobals.current.name, {
        page: this.page,
      });
    }

    this.deploys = this.sortDeploys(
      this.deployHelperService.processDeploys(cloneDeep(this.deploys)),
      this.column,
      this.sortDesc,
    );
    this.chartData = this.deploys;
  }

  private loadEnvironments(projectId: any) {
    return this.attributeService
      .query({ projectId, limit: 50, attribute: "context.environment" })
      .pipe(
        map((data: any) => {
          this.environments =
            data.attributes != null
              ? data.attributes.map((attr: any) => attr.value.environment)
              : undefined;
          this.environments = this.environments || [];
          this.environments.push("all");
        }),
        catchError(
          (_error: HttpErrorResponse) => (this.environments = ["all"]),
        ),
      );
  }

  private loadPage() {
    if (this.page == null) {
      this.page = 1;
    }
    if (!isFinite(this.page) || this.page < 1) {
      this.page = 1;
      return this.$state.go(this.$uiRouterGlobals.current.name, {
        page: this.page,
      });
    }

    const params: any = {
      projectId: this.project.id,
      limit: this.PAGESIZE,
      page: this.page,
      created_at__gte: this.timeDurAgoService.timeFor(this.dur, this.lastN),
    };

    if (this.environment != null && this.environment !== "all") {
      params.environment = this.environment;
    }

    this.loading = true;
    this.loadingChanged.emit(1);

    this.deployService
      .query(params)
      .pipe(
        mergeMap((data: any) => {
          if (!data.deploys || data.deploys.length === 0) {
            return of(data);
          }
          return this.addFirstDeployAge(data).pipe(
            mergeMap(this.setDiffStats.bind(this)),
          );
        }),
      )
      .pipe(
        finalize(() => {
          this.loading = false;
          this.loadingChanged.emit(-1);
        }),
      )
      .subscribe(
        (data: any) => {
          this.displayPage(data);
        },
        (error: HttpErrorResponse) => {
          if (error.status === 404) {
            return;
          }
        },
      );
  }

  private setDiffStats(data: any) {
    const revisions = [] as any;
    const deployIndices = [] as number[];
    const prevDeployObservables = [] as any;
    const deployData = [] as any;

    for (const deploy of data.deploys) {
      deploy.totalChanges = 0;

      const filteredDeploys = data.deploys.filter(
        (d: any) => d.environment === deploy.environment,
      );
      const deployIndex = filteredDeploys.indexOf(deploy);
      if (deployIndex === -1) {
        continue;
      }

      const prevDeploy = filteredDeploys[deployIndex + 1];
      if (prevDeploy) {
        // The previous deploy is available on the same page, use its revision.
        revisions.push([prevDeploy.revision, deploy.revision]);
        deployIndices.push(data.deploys.indexOf(deploy));
      } else {
        // The previous deploy is not available on the same page so we have to
        // fetch it.
        prevDeployObservables.push(
          this.deployService.prev({
            projectId: this.project.id,
            deployId: deploy.id,
          }),
        );
        deployData.push({
          deployIndex: data.deploys.indexOf(deploy),
          revision: deploy.revision,
        });
      }
    }

    if (prevDeployObservables.length === 0) {
      return this.fetchDiffStats(data, revisions, deployIndices);
    }

    // Fetch previous deploys which are not on the same page to get revisions
    // of previous deploys.
    return forkJoin(prevDeployObservables).pipe(
      mergeMap((prevDeploys: any) => {
        for (let i = 0; i < prevDeploys.length; i++) {
          revisions.push([prevDeploys[i].revision, deployData[i].revision]);
          deployIndices.push(deployData[i].deployIndex);
        }
        return this.fetchDiffStats(data, revisions, deployIndices);
      }),
      catchError(() => this.fetchDiffStats(data, revisions, deployIndices)),
    );
  }

  private fetchDiffStats(data: any, revisions: any, deployIndices: any) {
    return this.diffStatsService
      .list({ projectId: this.project.id, revisions })
      .pipe(
        map((diffStats: any) => {
          for (const deployIndex of deployIndices) {
            const stats = diffStats.shift();

            data.deploys[deployIndex].diffStats = stats;
            if (!stats) {
              continue;
            }

            data.deploys[deployIndex].totalChanges =
              stats.additions + stats.deletions;
          }
          return data;
        }),
        catchError(() => of(data)),
      );
  }

  private addFirstDeployAge(data: any) {
    if (data.page === 1) {
      return of(data);
    }
    const nextDeployObvservables = [];
    for (const deploy of data.deploys) {
      const filteredDeploys = data.deploys.filter(
        (d: any) => d.environment === deploy.environment,
      );
      const deployIndex = filteredDeploys.indexOf(deploy);
      const nextDeploy = filteredDeploys[deployIndex - 1];
      // The next deploy is already on the page, so we don't need to do anything
      if (nextDeploy) {
        continue;
      }
      // Fetch the next deploy since it's not on the current page
      nextDeployObvservables.push(
        this.deployService
          .next({
            projectId: this.project.id,
            deployId: deploy.id,
          })
          .pipe(
            tap((next: any) => {
              deploy.deployAge = this.deployHelperService.calculateDeployAge(
                deploy,
                next,
              );
            }),
          ),
      );
    }
    return forkJoin(nextDeployObvservables).pipe(map(() => data));
  }

  private findEnvsWithNoDeploys(projectId: any, envs: any) {
    const observables = [];
    for (const env of envs) {
      if (env === "all") {
        continue;
      }
      observables.push(this.envHasDeploy(env, projectId));
    }
    return forkJoin(observables);
  }

  private envHasDeploy(env: string, projectId: any) {
    const params = {
      projectId,
      limit: 1,
      environment: env,
    };
    return this.deployService.query(params).pipe(
      map((data: any) => {
        if (data.count === 0) {
          return this.envsWithNoDeploys.push(env);
        }
      }),
    );
  }

  private buildSampleDeploys(): any {
    const deploys = [];
    const time = new Date().getTime();

    for (let i = 0; i < this.PAGESIZE; i++) {
      const sha = Math.random().toString(36).substring(7);
      deploys.push({
        id: `100${i}`,
        userId: 1 + i,
        projectId: 1,
        environment: "production",
        version: sha,
        username: "user",
        email: "",
        gravatarId: "",
        repository: "https://github.com/airbrake/airbrake",
        revision: sha,
        noticeTotalCount: Math.round(Math.random() * 1000),
        groupCreatedCount: Math.round(Math.random() * 100),
        groupResolvedCount: Math.round(Math.random() * 100),
        groupUnresolvedCount: Math.round(Math.random() * 50),
        createdAt: new Date(time - 300000 * i).toISOString(),
        updatedAt: new Date(time - 300000 * i).toISOString(),
        diffStats: {
          additions: Math.round(Math.random() * 100),
          deletions: Math.round(Math.random() * 100),
        },
      });
    }

    return { page: 1, count: this.PAGESIZE, deploys };
  }
}
